import { LocalStorageKey } from "@/helpers/interfaces/LocalStorageKey.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

/**
 * This hooks sets the start date of the reservation in the local storage
 * and clears it & engineStore if the date is in the past.
 * Useful for cases where user left the reservation open and a day has passed
 */
export const useCheckAndClearDataPreservation = () => {
  const { page, setPage, defaultDateRange, cartAccommodationItems, resetAll, isReserved } = useEngineStore(
    useShallow((state) => ({
      page: state.page,
      setPage: state.setPage,
      defaultDateRange: state.defaultDateRange,
      cartAccommodationItems: state.cartAccommodationItems,
      resetAll: state.resetAll,
      isReserved: state.isReserved,
    })),
  );

  // Set the reservation start date in the local storage on unload
  useEffect(() => {
    const addToLocalStorage = () => {
      const startDate = defaultDateRange[0];
      if (startDate) {
        localStorage.setItem(
          LocalStorageKey.SwenBookingEngineReservationStartDate,
          JSON.stringify({ startDate }),
        );
      }
    };
    window.addEventListener("beforeunload", addToLocalStorage);
    return () => {
      window.removeEventListener("beforeunload", addToLocalStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if the reservation start date is in the past, clear it & resetAll if true
  // if non-existant --> set it
  useEffect(() => {
    const startDate = localStorage.getItem(LocalStorageKey.SwenBookingEngineReservationStartDate);
    if (startDate) {
      const parsedJSON = JSON.parse(startDate);
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      if (new Date(parsedJSON.startDate) < startOfToday) {
        localStorage.removeItem(LocalStorageKey.SwenBookingEngineReservationStartDate);
        resetAll();
        setPage(AppPage.AccommodationPicking, "replace");
      }
    } else {
      if (cartAccommodationItems.length > 0)
        localStorage.setItem(
          LocalStorageKey.SwenBookingEngineReservationStartDate,
          JSON.stringify({ startDate: defaultDateRange[0] }),
        );
    }
  }, [page, setPage, cartAccommodationItems, resetAll, defaultDateRange]);

  // resets state if previously reservation was completed
  useEffect(() => {
    if (isReserved) {
      resetAll();
      setPage(AppPage.AccommodationPicking, "replace");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
